'use client';

/**
 * SsnRenderer Component
 *
 * Renders SSN field type with masking and visibility toggle.
 * Supports display, edit, and inline modes.
 *
 * @see Issue #432 - Templated Loan Intake Wizard
 */

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { NeonAccent } from '../../../theme/palette';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { MaskedInput } from '../../MaskedInput';

// ============================================================================
// Types
// ============================================================================

export interface SsnRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value (unmasked digits) */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const DisplayValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  letterSpacing: '0.1em',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary,
}));

const MaskedDisplayValue = styled('span')(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
}));

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format SSN for display (masked by default, showing only last 4)
 */
const formatSsnDisplay = (value: string | null | undefined, showFull: boolean = false): string => {
  if (!value) return '-';

  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 0) return '-';

  if (showFull && digits.length === 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }

  // Masked: show last 4 only
  if (digits.length >= 4) {
    return `•••-••-${digits.slice(-4)}`;
  }

  return '•'.repeat(digits.length);
};

// ============================================================================
// Component
// ============================================================================

export const SsnRenderer: React.FC<SsnRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled,
  size = 'medium',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Reserved for future enhancement
  neonAccent: _neonAccent,
  className,
}) => {
  const stringValue = value != null ? String(value).replace(/\D/g, '') : '';

  // Display mode - show masked value
  if (mode === 'display') {
    const displayValue = formatSsnDisplay(stringValue);
    const isFullyMasked = displayValue.startsWith('•');

    return (
      <Box className={className}>
        <DisplayValue variant={size === 'small' ? 'body2' : 'body1'}>
          {isFullyMasked ? (
            <>
              <MaskedDisplayValue>•••-••-</MaskedDisplayValue>
              {stringValue.slice(-4) || '••••'}
            </>
          ) : (
            displayValue
          )}
        </DisplayValue>
      </Box>
    );
  }

  // Edit/Inline mode - show masked input
  const handleChange = (newValue: string) => {
    onChange?.(newValue);
  };

  const sizeMap = {
    small: 'small' as const,
    medium: 'medium' as const,
    large: 'medium' as const,
  };

  return (
    <Box className={className}>
      <MaskedInput
        mask="ssn"
        value={stringValue}
        onChange={handleChange}
        label={field.label}
        placeholder={field.placeholder}
        helperText={error || field.helpText}
        error={Boolean(error)}
        disabled={disabled || field.readOnly}
        required={field.required}
        size={sizeMap[size]}
        color="primary"
        showToggle
        initialVisible={false}
      />
    </Box>
  );
};

SsnRenderer.displayName = 'SsnRenderer';

export default SsnRenderer;
